import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_APP_API_URL;
const AUTH_ENDPOINT_FRAGMENT = '/api/auth/community/';
const AUTH_REFRESH_PATH = '/api/auth/community/refresh/';

const instance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  xsrfCookieName: 'csrftoken',
  xsrfHeaderName: 'X-CSRFToken',
});

const setHeader = (headers, key, value) => {
  if (!headers || value === undefined) {
    return;
  }
  if (typeof headers.set === 'function') {
    headers.set(key, value);
  } else {
    headers[key] = value;
  }
};

const readCookie = (name) => {
  if (typeof document === 'undefined') {
    return null;
  }
  const value = document.cookie
    .split('; ')
    .find((row) => row.startsWith(`${name}=`));
  return value ? decodeURIComponent(value.split('=')[1]) : null;
};

instance.interceptors.request.use((config) => {
  config.withCredentials = true;
  const requestId = typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  setHeader(config.headers, 'X-Request-ID', requestId);

  const csrfToken = readCookie('csrftoken');
  if (csrfToken) {
    setHeader(config.headers, 'X-CSRFToken', csrfToken);
  }

  return config;
});

const normaliseMediaLinks = (response) => {
  if (!API_BASE_URL) {
    return response;
  }
  const apiHost = new URL(API_BASE_URL).host;

  const fixMediaUrl = (url) => {
    if (typeof url === 'string' && url.includes('/media/')) {
      return url.replace(/(\/\/[^/]+)(\/media\/)/, `//${apiHost}$2`);
    }
    return url;
  };

  const fixObject = (value) => {
    if (!value || typeof value !== 'object') {
      return value;
    }
    if (Array.isArray(value)) {
      return value.map(fixObject);
    }

    const output = {};
    for (const key of Object.keys(value)) {
      if (['avatar', 'avatar_url', 'cover_image', 'cover_url', 'coverImage', 'image'].includes(key)) {
        output[key] = fixMediaUrl(value[key]);
      } else if (typeof value[key] === 'object') {
        output[key] = fixObject(value[key]);
      } else {
        output[key] = value[key];
      }
    }
    return output;
  };

  if (response.data) {
    response.data = fixObject(response.data);
  }
  return response;
};

let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

instance.interceptors.response.use(
  (response) => normaliseMediaLinks(response),
  async (error) => {
    const originalRequest = error.config || {};
    const status = error.response?.status;
    const requestUrl = originalRequest.url || '';
    const isAuthRequest = requestUrl.includes(AUTH_ENDPOINT_FRAGMENT);

    // Attempt a silent token refresh on the first 401 from a non-auth endpoint.
    // skipAuthRefresh is set on the refresh call itself to prevent infinite loops.
    if (
      status === 401 &&
      !originalRequest._retry &&
      !originalRequest.skipAuthRefresh &&
      !isAuthRequest
    ) {
      if (isRefreshing) {
        return new Promise(function(resolve, reject) {
          failedQueue.push({ resolve, reject });
        })
          .then(() => {
            return instance(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await instance.post(AUTH_REFRESH_PATH, {}, { skipAuthRefresh: true });
        
        isRefreshing = false;
        processQueue(null);

        // Refresh succeeded — retry the original request.
        return instance(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        processQueue(refreshError, null);

        // Refresh token is expired or invalid — the session is truly over.
        if (typeof window !== 'undefined') {
          window.dispatchEvent(new Event('community:auth-expired'));
        }
        return Promise.reject(refreshError);
      }
    }

    // Fire auth-expired only when:
    // - it's a 401 on an auth endpoint itself (e.g. the refresh call returned 401)
    // - AND it wasn't a skipAuthRefresh call (which handles its own error above)
    if (
      status === 401 &&
      typeof window !== 'undefined' &&
      isAuthRequest &&
      !originalRequest.skipAuthRefresh
    ) {
      window.dispatchEvent(new Event('community:auth-expired'));
    }

    return Promise.reject(error);
  }
);

export default instance;
